import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import type { TournamentCardFavorite } from '@/components/TournamentCard';
import { PARTICIPANT_STATUSES, TOURNAMENT_FAVORITE_LISTS } from '@/constants';
import type { TournamentFavoriteList } from '@/constants';
import { SOURCE_KEY } from '@/hooks/useTournamentList';
import { apiClient } from '@/services/api';
import type { Tournament, TournamentListSource } from '@/types';

/** Lists that keep favorites; the archive has none. */
const FAVORITE_LIST: Partial<Record<TournamentListSource, TournamentFavoriteList>> = {
  my: TOURNAMENT_FAVORITE_LISTS.MY,
  public: TOURNAMENT_FAVORITE_LISTS.PUBLIC,
};

/**
 * Favorite toggle of a tournament list: returns, per row, the props of its
 * toggle - or nothing where the row has none (the archive, a tournament the
 * user is blocked in). A change refetches every query of that list, so the
 * tournament moves to its new place; a mark over the limit (409) is explained
 * by a toast.
 */
export function useTournamentFavorite(
  source: TournamentListSource,
): (tournament: Tournament) => TournamentCardFavorite | undefined {
  const { t } = useTranslation(['tournament', 'common']);
  const queryClient = useQueryClient();
  const list = FAVORITE_LIST[source];

  const { mutate, isPending } = useMutation({
    mutationFn: ({ tournamentId, active }: { tournamentId: number; active: boolean }) => {
      if (!list) throw new Error(`The ${source} list has no favorites`);
      return active
        ? apiClient.setTournamentFavorite(tournamentId, list)
        : apiClient.removeTournamentFavorite(tournamentId, list);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [SOURCE_KEY[source]] }),
    onError: (error: any) => {
      toast.error(
        error.response?.status === 409
          ? t('tournament:favorites.limitReached')
          : t('common:somethingWentWrong'),
      );
    },
  });

  return useCallback(
    (tournament: Tournament) => {
      if (!list || tournament.userStatus === PARTICIPANT_STATUSES.BLOCKED) return undefined;
      const active = Boolean(tournament.isFavorite);
      return {
        active,
        onToggle: () => mutate({ tournamentId: tournament.id, active: !active }),
        disabled: isPending,
      };
    },
    [list, mutate, isPending],
  );
}
